import React from 'react';
import {
  Show,
  TabbedShowLayout,
  Tab,
  TextField,
  DateField,
  FunctionField,
  ChipField,
} from 'react-admin';
import ReactMarkdown from 'react-markdown';
import 'prism-themes/themes/prism-one-light.css';

const roleChoices = [
  { id: 'user', name: '👤 Пользователь' },
  { id: 'assistant', name: '🤖 Ассистент' },
  { id: 'system', name: '⚙️ Система' },
];

const RoleChip = ({ record }: any) => {
  if (!record || !record.role) return <span>-</span>;
  const roleChoice = roleChoices.find(choice => choice.id === record.role);
  return <ChipField record={{ value: roleChoice?.name || record.role }} source="value" />;
};

export const ChatMessageShow = () => (
  <Show>
    <TabbedShowLayout>
      <Tab label="Основная информация">
        <TextField source="id" label="ID" />
        <FunctionField 
          label="Роль отправителя"
          render={(record: any) => <RoleChip record={record} />}
        />
        
        {/* Пользователь */}
        <FunctionField 
          label="Пользователь"
          render={(record: any) => record?.user?.name || 'Система'}
        />
        <FunctionField 
          label="Email пользователя"
          render={(record: any) => record?.user?.email || '-'}
        />
        
        {/* Содержимое */}
        <FunctionField 
          label="Содержимое сообщения"
          render={(record: any) => {
            if (!record.content) {
              return <div style={{ padding: '16px', color: '#666' }}>Нет содержимого</div>;
            }

            return (
              <div style={{
                backgroundColor: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                marginTop: '8px',
                maxHeight: '600px',
                overflowY: 'auto'
              }}>
                {/* Заголовок с переключателем вида */}
                <div style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #e9ecef',
                  backgroundColor: '#f1f3f4',
                  borderRadius: '8px 8px 0 0',
                  fontSize: '0.9em',
                  fontWeight: 'bold',
                  color: '#5f6368'
                }}>
                  📝 Содержимое сообщения
                </div>
                
                {/* Markdown содержимое */}
                <div style={{
                  padding: '16px',
                  lineHeight: '1.6',
                  fontSize: '14px'
                }}>
                  <ReactMarkdown
                    components={{
                      // Стили для кода
                      code: ({ node, inline, className, children, ...props }: any) => {
                        if (inline) {
                          return (
                            <code
                              style={{
                                backgroundColor: '#f1f3f4',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '0.9em',
                                fontFamily: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                              }}
                              {...props}
                            >
                              {children}
                            </code>
                          );
                        }
                        return (
                          <pre
                            style={{
                              backgroundColor: '#f8f8f8',
                              border: '1px solid #e1e4e8',
                              borderRadius: '6px',
                              padding: '16px',
                              overflow: 'auto',
                              fontSize: '14px',
                              lineHeight: '1.45',
                              fontFamily: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                            }}
                          >
                            <code {...props}>{children}</code>
                          </pre>
                        );
                      },
                      // Стили для заголовков
                      h1: ({ children }) => (
                        <h1 style={{ color: '#1a73e8', borderBottom: '2px solid #e8f0fe', paddingBottom: '8px' }}>
                          {children}
                        </h1>
                      ),
                      h2: ({ children }) => (
                        <h2 style={{ color: '#1a73e8', borderBottom: '1px solid #e8f0fe', paddingBottom: '6px' }}>
                          {children}
                        </h2>
                      ),
                      h3: ({ children }) => (
                        <h3 style={{ color: '#1a73e8' }}>{children}</h3>
                      ),
                      // Стили для списков
                      ul: ({ children }) => (
                        <ul style={{ paddingLeft: '20px', marginBottom: '16px' }}>
                          {children}
                        </ul>
                      ),
                      ol: ({ children }) => (
                        <ol style={{ paddingLeft: '20px', marginBottom: '16px' }}>
                          {children}
                        </ol>
                      ),
                      // Стили для ссылок
                      a: ({ href, children }) => (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#1a73e8',
                            textDecoration: 'none'
                          }}
                          onMouseEnter={(e) => (e.target as HTMLElement).style.textDecoration = 'underline'}
                          onMouseLeave={(e) => (e.target as HTMLElement).style.textDecoration = 'none'}
                        >
                          {children}
                        </a>
                      ),
                      // Стили для цитат
                      blockquote: ({ children }) => (
                        <blockquote
                          style={{
                            borderLeft: '4px solid #1a73e8',
                            paddingLeft: '16px',
                            margin: '16px 0',
                            color: '#5f6368',
                            fontStyle: 'italic'
                          }}
                        >
                          {children}
                        </blockquote>
                      ),
                    }}
                  >
                    {record.content}
                  </ReactMarkdown>
                </div>
              </div>
            );
          }}
        />
        
        {/* Даты */}
        <DateField source="createdAt" label="Дата создания" showTime />
        <DateField source="updatedAt" label="Дата обновления" showTime />
      </Tab>

      <Tab label="Display Data">
        <FunctionField 
          label="Данные для отображения"
          render={(record: any) => {
            if (!record.displayData) {
              return <div style={{ padding: '16px', color: '#666' }}>Нет данных для отображения</div>;
            }
            
            try {
              const data = JSON.parse(record.displayData);
              return (
                <div style={{
                  backgroundColor: '#f5f5f5',
                  padding: '16px',
                  borderRadius: '8px',
                  marginTop: '8px',
                  fontFamily: 'monospace',
                  fontSize: '0.9em',
                  maxHeight: '500px',
                  overflowY: 'auto'
                }}>
                  <pre>{JSON.stringify(data, null, 2)}</pre>
                </div>
              );
            } catch (error) {
              return (
                <div style={{ padding: '16px', color: '#f44336' }}>
                  Ошибка парсинга JSON: {(error as Error).message}
                </div>
              );
            }
          }}
        />
      </Tab>

      <Tab label="Tool Calls">
        <FunctionField 
          label="Вызовы инструментов"
          render={(record: any) => {
            if (!record.toolCalls) {
              return <div style={{ padding: '16px', color: '#666' }}>Нет вызовов инструментов</div>;
            }
            
            try {
              const calls = JSON.parse(record.toolCalls);
              return (
                <div style={{
                  backgroundColor: '#f5f5f5',
                  padding: '16px',
                  borderRadius: '8px',
                  marginTop: '8px',
                  fontFamily: 'monospace',
                  fontSize: '0.9em',
                  maxHeight: '500px',
                  overflowY: 'auto'
                }}>
                  <pre>{JSON.stringify(calls, null, 2)}</pre>
                </div>
              );
            } catch (error) {
              return (
                <div style={{ padding: '16px', color: '#f44336' }}>
                  Ошибка парсинга JSON: {(error as Error).message}
                </div>
              );
            }
          }}
        />
      </Tab>
    </TabbedShowLayout>
  </Show>
);
